import { Component, Inject, OnDestroy, OnInit } from "@angular/core";
import { Router, RouterLink, RouterOutlet } from "@angular/router";
import { MatButtonModule } from "@angular/material/button";
import { MessageCenterService } from "./shared/services/messageCenter.service";
import { MessageCenter } from "./shared/interfaces/messageCenter.interface";
import { getDictionaryItem } from "./shared/utils/dictionary.domain";
import { MessageCenterComponent } from "./shared/components/message-center/message-center.component";
import { AccountPickerComponent } from "./shared/components/account-picker/account-picker.component";
import { UserProfileComponent } from "./shared/components/user-profile/user-profile.component";
import { MessageBannerState } from "./shared/interfaces/messageCenter.interface";
import { MSAL_GUARD_CONFIG, MsalGuardConfiguration, MsalService, MsalBroadcastService } from "@azure/msal-angular";
import {
    AuthenticationResult,
    InteractionStatus,
    PopupRequest,
    RedirectRequest,
    EventMessage,
    EventType,
    InteractionType,
    AccountInfo,
    SsoSilentRequest,
    IdTokenClaims,
    PromptValue
} from "@azure/msal-browser";
import { Subject } from "rxjs";
import { filter, takeUntil } from "rxjs/operators";
import { environment } from "../environments/environment";
import { AccountsService } from "./shared/services/accounts.service";
import { Account } from "./shared/interfaces/accounts.interface";
import { UserContact, UserAccountId } from "./shared/interfaces/user-info.interface";
import { ErrorService } from "./shared/services/errorService.service";
import { CdkTrapFocus } from "@angular/cdk/a11y";

type IdTokenClaimsWithPolicyId = IdTokenClaims & {
    acr?: string;
    tfp?: string;
};

@Component({
    selector: "app-root",
    standalone: true,
    imports: [RouterOutlet, MatButtonModule, AccountPickerComponent, MessageCenterComponent, UserProfileComponent, RouterLink, CdkTrapFocus],
    templateUrl: "./app.component.html",
    styleUrl: "./app.component.scss"
})
export class AppComponent implements OnInit, OnDestroy {
    messages: MessageCenter[] = [];
    messageCenterState = MessageBannerState.success;
    loginDisplay: boolean = false;
    //account: Account | null = null;
    userContact: UserContact | null = null;
    userAccounts: UserAccountId[] | null = null;
    //showAccountPicker: boolean = false;
    // Sets a temporary timer before showing the error message if the user has no accounts
    userAccountErrorTimer: boolean = false;
    // Sets a temporary timer before showing login screen
    loginTimer: boolean = false;

    headerId: string = "header";
    loginButtonId: string = "login-button";
    logoutButtonId: string = "logout-button";
    navigationId: string = "navigation-bar";
    isInline: boolean = false;
    private readonly _destroying$ = new Subject<void>();
    errorHeader: string = getDictionaryItem("anErrorOccurred");
    errorMessage: string = "";
    isTestCafe = typeof window !== "undefined" && ("%testCafeAutomation%" in window || window.location.search.includes("testcafe=1"));

    constructor(
        private messageService: MessageCenterService,
        private router: Router,
        @Inject(MSAL_GUARD_CONFIG) private msalGuardConfig: MsalGuardConfiguration,
        private authService: MsalService,
        private msalBroadcastService: MsalBroadcastService,
        private accountService: AccountsService,
        private errorService: ErrorService
    ) {}

    get isLoginPage(): boolean {
        return this.router.url.includes("/login");
    }

    get isLoggedIn(): boolean {
        try {
            return this.authService.instance.getAllAccounts().length > 0;
        } catch (error) {
            return false;
        }
    }

    setUserAndAccount() {
        try {
            if (!this.loginDisplay || this.authService.instance.getAllAccounts().length === 0) {
                return;
            }
        } catch (error) {
            return;
        }

        // Ensure we have an active account before proceeding
        this.checkAndSetActiveAccount();

        let activeAccount = this.authService.instance.getActiveAccount();

        // MSAL 4: If account exists but lacks claims, try to hydrate it
        if (activeAccount && (!activeAccount.idTokenClaims || !activeAccount.idTokenClaims.emails)) {
            this.hydrateAccountSilently(activeAccount)
                .then(() => {
                    // Re-read the account after hydration and continue
                    const hydratedAccount = this.authService.instance.getActiveAccount();
                    this.processUserAccount(hydratedAccount);
                })
                .catch((error) => {
                    console.warn("Account hydration failed:", error);
                    // Continue with current account even if hydration failed
                    this.processUserAccount(activeAccount);
                });
        } else {
            this.processUserAccount(activeAccount);
        }
    }

    private processUserAccount(activeAccount: AccountInfo | null) {
        let activeEmail = activeAccount?.idTokenClaims?.emails?.[0] || activeAccount?.username || null;

        if (!activeEmail) {
            // Try UserInfo fallback
            this.fetchUserInfoEmail()
                .then((emailFromUserInfo) => {
                    if (emailFromUserInfo) {
                        activeEmail = emailFromUserInfo;
                    }
                    this.continueWithEmail(activeEmail);
                })
                .catch((e) => {
                    console.warn("userinfo fallback failed", e);
                    this.continueWithEmail(activeEmail);
                });
        } else {
            this.continueWithEmail(activeEmail);
        }
    }

    private continueWithEmail(activeEmail: string | null) {
        if (!activeEmail) {
            this.errorHeader = getDictionaryItem("noAccountsFoundHeader");
            this.errorMessage = getDictionaryItem("noAccountsFoundText");
            return;
        }

        this.accountService.getContactPerson(activeEmail).subscribe((contact: UserContact[]) => {
            if (contact && contact.length > 0) {
                // The reason for using [0] is because the API returns an array of contacts, but the match will always just be one
                this.userContact = contact[0];

                if (typeof window !== "undefined" && contact && localStorage.getItem("accountid") === null) {
                    localStorage.setItem("accountid", contact[0].currentAccount.accountId);
                    localStorage.setItem("contactid", contact[0].contactId);
                }

                this.accountService.getContactPerson(activeEmail, localStorage.getItem("contactid")).subscribe((contact: UserContact[]) => {
                    if (!contact) return;
                    if (contact[0].hasWebAccess === false) {
                        this.errorHeader = getDictionaryItem("noAccountsFoundHeader");
                        this.errorMessage = getDictionaryItem("noAccountsFoundText");
                        this.userAccounts = null;
                        throw new Error("No contact found");
                    }
                });

                this.userAccounts = contact
                    .filter((c) => c.hasWebAccess === true)
                    .map((c) => ({
                        accountId: c.currentAccount.accountId,
                        contactid: c.contactId,
                        parentcustomerid_account: {
                            accountid: c.currentAccount.accountId,
                            accountnumber: c.currentAccount.accountNumber,
                            name: c.currentAccount.name
                        }
                    }));
                this.userAccounts.sort((a, b) => a.parentcustomerid_account.name.localeCompare(b.parentcustomerid_account.name));
            } else {
                this.userAccountErrorTimer = true;
                this.userAccounts = null;
                this.errorHeader = getDictionaryItem("noActiveContactHeader");
                this.errorMessage = getDictionaryItem("noActiveContactText");
                throw new Error("No contact found");
            }
        });
    }

    /**
     * Attempts to retrieve the user's email via the OIDC UserInfo endpoint using scopes openid/profile/email.
     * Reference: https://learn.microsoft.com/en-us/entra/identity-platform/userinfo#calling-the-api
     */
    private fetchUserInfoEmail(): Promise<string | null> {
        return new Promise((resolve) => {
            try {
                const account = this.authService.instance.getActiveAccount();
                if (!account) {
                    resolve(null);
                    return;
                }

                const tokenRequest: any = {
                    scopes: ["openid", "profile", "email"],
                    account
                };

                this.authService.acquireTokenSilent(tokenRequest).subscribe({
                    next: async (result) => {
                        const accessToken = result?.accessToken;
                        if (!accessToken) {
                            resolve(null);
                            return;
                        }

                        try {
                            const response = await fetch("https://graph.microsoft.com/oidc/userinfo", {
                                method: "GET",
                                headers: { Authorization: `Bearer ${accessToken}` }
                            });

                            if (!response.ok) {
                                resolve(null);
                                return;
                            }

                            const data = await response.json();
                            resolve(data?.email || data?.emails?.[0] || data?.preferred_username || null);
                        } catch (error) {
                            console.warn("fetchUserInfoEmail fetch error", error);
                            resolve(null);
                        }
                    },
                    error: (error) => {
                        console.warn("fetchUserInfoEmail error", error);
                        resolve(null);
                    }
                });
            } catch (error) {
                console.warn("fetchUserInfoEmail error", error);
                resolve(null);
            }
        });
    }

    ngOnInit() {
        this.authService.handleRedirectObservable().subscribe();
        // Check if the app is inlined (loaded within an iframe)
        if (typeof window != "undefined") {
            this.isInline = window !== window.parent;
        }

        // MSAL 4: Ensure proper initialization before using any MSAL APIs
        this.initializeMsalProperly();

        this.msalBroadcastService.msalSubject$
            .pipe(filter((msg: EventMessage) => msg.eventType === EventType.ACCOUNT_ADDED || msg.eventType === EventType.ACCOUNT_REMOVED))
            .subscribe((result: EventMessage) => {
                try {
                    if (this.authService.instance.getAllAccounts().length === 0) {
                        window.location.pathname = "/";
                    } else {
                        this.setLoginDisplay();
                    }
                } catch (error) {
                    console.warn("MSAL not ready for account operations:", error);
                }
            });

        this.msalBroadcastService.inProgress$
            .pipe(
                filter((status: InteractionStatus) => status === InteractionStatus.None),
                takeUntil(this._destroying$)
            )
            .subscribe(() => {
                try {
                    this.setLoginDisplay();
                    this.checkAndSetActiveAccount();
                    this.setUserAndAccount();
                    this.getMessages();
                } catch (error) {
                    console.error("An error occurred:", error);
                }
            });

        this.msalBroadcastService.msalSubject$
            .pipe(
                filter(
                    (msg: EventMessage) =>
                        msg.eventType === EventType.LOGIN_SUCCESS || msg.eventType === EventType.ACQUIRE_TOKEN_SUCCESS || msg.eventType === EventType.SSO_SILENT_SUCCESS
                ),
                takeUntil(this._destroying$)
            )
            .subscribe((result: EventMessage) => {
                let payload = result.payload as AuthenticationResult;
                let idtoken = payload.idTokenClaims as IdTokenClaimsWithPolicyId;

                if (idtoken.acr === environment.b2cPolicies.names.signIn || idtoken.tfp === environment.b2cPolicies.names.signIn) {
                    this.authService.instance.setActiveAccount(payload.account);
                    try {
                        if (payload.account?.homeAccountId && typeof window !== "undefined") {
                            localStorage.setItem("activeHomeAccountId", payload.account.homeAccountId);
                        }
                    } catch {}
                }

                if (idtoken.acr === environment.b2cPolicies.names.editProfile || idtoken.tfp === environment.b2cPolicies.names.editProfile) {
                    // retrieve the account from initial sing-in to the app
                    const originalSignInAccount = this.authService.instance
                        .getAllAccounts()
                        .find(
                            (account: AccountInfo) =>
                                account.idTokenClaims?.oid === idtoken.oid &&
                                account.idTokenClaims?.sub === idtoken.sub &&
                                ((account.idTokenClaims as IdTokenClaimsWithPolicyId).acr === environment.b2cPolicies.names.signIn ||
                                    (account.idTokenClaims as IdTokenClaimsWithPolicyId).tfp === environment.b2cPolicies.names.signIn)
                        );

                    let signInFlowRequest: SsoSilentRequest = {
                        authority: environment.b2cPolicies.authorities.signIn.authority,
                        account: originalSignInAccount
                    };

                    // silently login again with the signIn policy
                    this.authService.ssoSilent(signInFlowRequest);
                }

                if (idtoken.acr === environment.b2cPolicies.names.resetPassword || idtoken.tfp === environment.b2cPolicies.names.resetPassword) {
                    let signInFlowRequest: RedirectRequest | PopupRequest = {
                        authority: environment.b2cPolicies.authorities.signIn.authority,
                        scopes: [...environment.apiConfig.scopes],
                        prompt: PromptValue.LOGIN // force user to reauthenticate with their new password
                    };

                    this.login(signInFlowRequest);
                }

                return result;
            });

        this.errorService.error$.subscribe((message) => {
            this.errorMessage = message;
        });
    }

    ngOnDestroy() {
        this._destroying$.next();
        this._destroying$.complete();
    }

    setLoginDisplay() {
        try {
            const accountCount = this.authService.instance.getAllAccounts().length;
            this.loginDisplay = accountCount > 0;
        } catch (error) {
            console.warn("setLoginDisplay error - MSAL may not be ready:", error);
            this.loginDisplay = false;
        }

        if (!this.loginDisplay) {
            setTimeout(() => {
                this.loginTimer = true;
            }, 100);
        } else {
            setTimeout(() => {
                this.userAccountErrorTimer = true;
            }, 2500);
        }
    }

    getMessages() {
        if (!this.loginDisplay || this.authService.instance.getAllAccounts().length === 0) return;
        this.messageService.getAllActiveMessages().subscribe((messages) => {
            if (messages && messages.length > 0) {
                this.messages = messages;
                return messages;
            } else return;
        });
    }

    getActiveCompany(): string | undefined {
        return this.userAccounts?.find((account) => account.parentcustomerid_account.accountid === localStorage.getItem("accountid"))?.parentcustomerid_account.name;
    }

    checkAndSetActiveAccount() {
        try {
            let activeAccount = this.authService.instance.getActiveAccount();

            if (!activeAccount && this.authService.instance.getAllAccounts().length > 0) {
                const accounts = this.authService.instance.getAllAccounts();
                let accountToActivate = accounts[0];
                try {
                    if (typeof window !== "undefined") {
                        const storedHomeId = localStorage.getItem("activeHomeAccountId");
                        if (storedHomeId) {
                            const matched = accounts.find((a) => a.homeAccountId === storedHomeId);
                            if (matched) accountToActivate = matched;
                        }
                    }
                } catch {}

                // MSAL 4: Ensure account is properly hydrated before setting as active
                this.authService.instance.setActiveAccount(accountToActivate);

                // MSAL 4: Verify the account was set correctly
                const verifyActiveAccount = this.authService.instance.getActiveAccount();
                if (!verifyActiveAccount) {
                    console.warn("Failed to set active account, trying fallback");
                    // Fallback: try to acquire token silently to hydrate account
                    this.hydrateAccountSilently(accounts[0]);
                }
            }
        } catch (error) {
            console.warn("Error in checkAndSetActiveAccount - MSAL may not be ready:", error);
        }
    }

    /**
     * MSAL 4: Ensure proper initialization before using any MSAL APIs
     * This prevents the "uninitialized_public_client_application" error
     */
    private async initializeMsalProperly() {
        try {
            // MSAL 4: Initialize the instance first
            await this.authService.instance.initialize();

            // Enable account storage events after initialization
            this.authService.instance.enableAccountStorageEvents();

            // Now it's safe to initialize account management
            this.initializeAccountManagement();
        } catch (error) {
            console.error("MSAL 4: Initialization failed:", error);
        }
    }

    private initializeAccountManagement() {
        try {
            const accounts = this.authService.instance.getAllAccounts();

            if (accounts.length > 0) {
                const storedHomeId = localStorage.getItem("activeHomeAccountId");
                if (storedHomeId) {
                    const matchedAccount = accounts.find((a) => a.homeAccountId === storedHomeId);
                    if (matchedAccount) {
                        this.authService.instance.setActiveAccount(matchedAccount);
                    }
                } else {
                    this.authService.instance.setActiveAccount(accounts[0]);
                }
            }
        } catch (error) {
            console.error("MSAL 4: Account management initialization failed:", error);
        }
    }

    private hydrateAccountSilently(account: AccountInfo): Promise<void> {
        return new Promise((resolve) => {
            try {
                const tokenRequest = {
                    scopes: ["openid", "profile", "email"],
                    account: account
                };

                this.authService.acquireTokenSilent(tokenRequest).subscribe({
                    next: () => {
                        resolve();
                    },
                    error: (error) => {
                        console.warn("Failed to hydrate account:", error);
                        resolve();
                    }
                });
            } catch (error) {
                console.warn("Failed to hydrate account:", error);
                resolve();
            }
        });
    }

    skipToContent(event: Event) {
        event.preventDefault();
        const mainContent = document.getElementById("main-content");
        if (mainContent) {
            mainContent.setAttribute("tabindex", "-1");
            mainContent.focus();
            mainContent.removeAttribute("tabindex");
        }
    }

    public login(userFlowRequest?: RedirectRequest | PopupRequest) {
        const interactionType = this.isTestCafe ? InteractionType.Redirect : InteractionType.Popup;
        if (this.msalGuardConfig.interactionType === interactionType) {
            if (this.msalGuardConfig.authRequest && interactionType === InteractionType.Popup) {
                this.authService.loginPopup({ ...this.msalGuardConfig.authRequest, ...userFlowRequest } as PopupRequest).subscribe({
                    next: (response: AuthenticationResult) => {
                        this.authService.instance.setActiveAccount(response.account);
                        try {
                            if (response.account?.homeAccountId && typeof window !== "undefined") {
                                localStorage.setItem("activeHomeAccountId", response.account.homeAccountId);
                                this.handlePostLoginRedirect();
                            }
                        } catch {}
                    },
                    error: (error) => {
                        console.error("Login failed: ", error);
                    }
                });
            } else if (this.msalGuardConfig.authRequest && interactionType === InteractionType.Redirect) {
                this.authService.loginRedirect({ ...this.msalGuardConfig.authRequest, ...userFlowRequest } as RedirectRequest);
            } else {
                this.authService.loginPopup(userFlowRequest).subscribe((response: AuthenticationResult) => {
                    this.authService.instance.setActiveAccount(response.account);
                    try {
                        if (response.account?.homeAccountId && typeof window !== "undefined") {
                            localStorage.setItem("activeHomeAccountId", response.account.homeAccountId);
                        }
                    } catch {}
                });
            }
        } else {
            if (this.msalGuardConfig.authRequest) {
                this.authService.loginRedirect({ ...this.msalGuardConfig.authRequest, ...userFlowRequest } as RedirectRequest);
            } else {
                this.authService.loginRedirect(userFlowRequest);
            }
        }
    }

    public logout() {
        const interactionType = this.isTestCafe ? InteractionType.Redirect : InteractionType.Popup;
        if (this.msalGuardConfig.interactionType === interactionType) {
            this.authService.logoutPopup({
                mainWindowRedirectUri: "/"
            });
            localStorage.removeItem("accountid");
            localStorage.removeItem("contactid");
        } else {
            this.authService.logoutRedirect();
            localStorage.removeItem("accountid");
            localStorage.removeItem("contactid");
        }
    }

    private handlePostLoginRedirect() {
        if (typeof window === "undefined") return;
        try {
            const stored = sessionStorage.getItem("postLoginRedirectUrl");
            if (!stored) return;

            sessionStorage.removeItem("postLoginRedirectUrl");
            if (!stored.startsWith("/")) return;

            const current = this.router.url || "/";

            const neutralPaths = ["/", "/login"];
            if (neutralPaths.includes(current)) {
                this.router.navigateByUrl(stored).catch(() => {});
            }
        } catch (e) {
            // Remove key to prevent redirect loops
            try {
                sessionStorage.removeItem("postLoginRedirectUrl");
            } catch {}
        }
    }

    public getDictionaryItem = getDictionaryItem;
}
